/**
 * The format modules.
 *
 * Every format runs the same three phases:
 *   partition — carve the round into competitive units
 *   tabulate  — walk the holes and produce a UnitTab
 *   settle    — rank the tabs and hand out points and cash
 *
 * What is uniform across formats is the PROJECTION, not the scoring. A
 * match-play margin, a quota surplus and a net total are genuinely different
 * numbers; pretending otherwise is what makes "uniform" format code sprout
 * special cases. So each format keeps its own shape internally and exposes a
 * `rankValue` (always higher-is-better) plus a `StatusChip`. The leaderboard
 * renders those with zero format knowledge.
 */

import { quotaTarget } from './handicap'
import { rankAndAward, type TiebreakContext } from './rank'
import {
  ballFor,
  compareBalls,
  countersForPlayers,
  PENDING,
  sum,
  thruFor,
  unitId,
  type Ball,
} from './scoring'
import type {
  Award,
  FormatConfig,
  Group,
  HoleScoreValue,
  HoleTab,
  IndividualMatchPlayConfig,
  Match,
  Pairing,
  PlayerId,
  QuotaConfig,
  RoundContext,
  Settlement,
  StrokeNetConfig,
  TeamHighLowConfig,
  TiebreakResolution,
  UnitTab,
} from './types'

export interface MatchResult {
  tabs: UnitTab[]
  settlement: Settlement
}

/**
 * Each player's 1-based seeding position, for formats that pay by flight.
 *
 * It has to be handed in rather than worked out here, because a flight is a
 * STANDINGS fact and a format module only ever sees one round. Deriving a seed
 * from the draw instead — tee-group index times four, plus a place in the
 * foursome — is what let the board show a man in Flight 1 while the ledger paid
 * him out of Flight 3, and put anyone in a group of three or five outside every
 * flight entirely. The caller passes the same positions the board renders.
 */
export type Seeding = Record<PlayerId, number>

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function pairingsFor(ctx: RoundContext, match: Match): Pairing[] {
  return ctx.pairings.filter((p) => p.matchId === match.id)
}

function groupOf(ctx: RoundContext, playerId: PlayerId): Group | undefined {
  return ctx.groups.find((g) => g.playerIds.includes(playerId))
}

function baseHoleTab(ctx: RoundContext, i: number, playerIds: PlayerId[]): HoleTab {
  const hole = ctx.holes[i]!
  const pops: Record<PlayerId, number> = {}
  const gross: Record<PlayerId, number | null> = {}
  const net: Record<PlayerId, number | null> = {}
  for (const pid of playerIds) {
    const p = ctx.pops[pid]?.[i] ?? 0
    const b = ballFor(ctx.scores[pid]?.[i], p)
    pops[pid] = p
    gross[pid] = b.gross
    net[pid] = b.net
  }
  return { holeNo: hole.number, par: hole.par, pops, gross, net, outcome: null }
}

/** "thru 7" / "F" / "—" */
function thruLabel(thru: number, total: number): string {
  if (thru === 0) return '—'
  return thru >= total ? 'F' : `thru ${thru}`
}

// ---------------------------------------------------------------------------
// match play (individual 1v1, team combined net, team four-ball — one margin)
// ---------------------------------------------------------------------------

interface MatchPlaySide {
  id: string
  playerIds: PlayerId[]
}

/**
 * How a side arrives at the one ball it plays against the other side.
 *
 * Three formats, one contest: the margin arithmetic, the closeout rule and the
 * chip are identical, and the entire difference between them fits in this
 * parameter. Forking the walk would give three places for "3 & 2" to drift.
 */
type SideBall = 'single' | 'combined' | 'lowBall'

/**
 * Walk a head-to-head contest hole by hole.
 */
function tabulateMatchPlay(
  ctx: RoundContext,
  a: MatchPlaySide,
  b: MatchPlaySide,
  closeout: boolean,
  counterBasis: 'gross' | 'net',
  mode: SideBall,
  /** The round was called on darkness — the contest ends as it stands. */
  called = false,
): [UnitTab, UnitTab] {
  const holes = ctx.holes
  const perHoleA: HoleTab[] = []
  const perHoleB: HoleTab[] = []
  let up = 0
  let won = 0
  let lost = 0
  let halved = 0
  let decidedAt: { up: number; toPlay: number } | null = null
  // Holes with a result, as distinct from the index of the furthest one.
  let scored = 0

  const ballOf = (side: MatchPlaySide, i: number): Ball =>
    mode === 'combined'
      ? combinedBall(ctx, side.playerIds, i)
      : mode === 'lowBall'
        ? lowBall(ctx, side.playerIds, i)
        : ballFor(ctx.scores[side.playerIds[0]!]?.[i], ctx.pops[side.playerIds[0]!]?.[i] ?? 0)

  for (let i = 0; i < holes.length; i++) {
    const ta = baseHoleTab(ctx, i, a.playerIds)
    const tb = baseHoleTab(ctx, i, b.playerIds)

    const ballA = ballOf(a, i)
    const ballB = ballOf(b, i)

    const outcome = decidedAt ? null : compareBalls(ballA, ballB)
    ta.outcome = outcome
    tb.outcome = outcome == null ? null : -outcome
    perHoleA.push(ta)
    perHoleB.push(tb)

    if (outcome != null && !decidedAt) {
      scored += 1
      if (outcome > 0) {
        up += 1
        won += 1
      } else if (outcome < 0) {
        up -= 1
        lost += 1
      } else halved += 1

      // A match up by more than the holes left is over — "5 & 3" — and must
      // stop moving, even though later holes are still recorded for counters.
      //
      // With nothing left to play there is nothing to close out: winning the
      // last hole is a 1 UP win, not "Won 1 & 0", and that is the commonest
      // margin in the book. The margin is the same either way; only the chip
      // and the closedOut flag were lying.
      /*
       * Holes with no result yet — which includes any gap BEHIND us.
       *
       * This counted from the current index, so a hole skipped earlier was
       * invisible to it and the closeout fired while the match was still
       * alive: 3 up with the 3rd never written down and 8 and 9 to come is
       * dormie with three to play, and it announced "Won 3 & 2". The other man
       * could still have halved it for $50 each. `scored` has always been the
       * count of decided holes; only `complete` was reading it.
       */
      const remaining = holes.length - scored
      if (closeout && remaining > 0 && Math.abs(up) > remaining) {
        decidedAt = { up: Math.abs(up), toPlay: remaining }
      }
    }
  }

  const remaining = holes.length - scored
  /*
   * Everything here counts HOLES, never the distance to the last one entered.
   *
   * There used to be a second counter, `played`, holding the 1-based index of
   * the furthest hole with a result. It reads 9 with the 5th missing and the
   * 9th in, so an eight-hole card called itself finished, a match with a gap
   * announced a closeout it had not earned, and the chip said "thru 9" on
   * eight holes of golf. Worse in a foursome, where a hole needs every ball: a
   * card with all nine posted but only the 4th answered by the opponents read
   * "1 DN thru 4" during the dry run.
   *
   * `scored` is the count, and the count is what "3 & 2", "thru 6" and
   * "complete" all actually mean. Nothing wanted the index, so it is gone.
   */
  /*
   * A called match is complete where it stands — but only if it ever stood
   * anywhere. `scored > 0` keeps a foursome that never teed off from being
   * paid out as a halved match by the same horn that ended real golf.
   */
  const complete = decidedAt != null || scored >= holes.length || (called && scored > 0)
  const calledIt = complete && decidedAt == null && scored < holes.length
  const countersA = countersForPlayers(ctx, a.playerIds, counterBasis)
  const countersB = countersForPlayers(ctx, b.playerIds, counterBasis)

  const label = (u: number): string => {
    if (decidedAt) {
      const w = u > 0
      return w
        ? `Won ${decidedAt.up} & ${decidedAt.toPlay}`
        : `Lost ${decidedAt.up} & ${decidedAt.toPlay}`
    }
    if (u === 0) return 'AS'
    return `${Math.abs(u)} ${u > 0 ? 'UP' : 'DN'}`
  }

  const mk = (
    side: MatchPlaySide,
    perHole: HoleTab[],
    u: number,
    counters: Record<PlayerId, ReturnType<typeof countersForPlayers>[string]>,
    w: number,
    l: number,
  ): UnitTab => ({
    unitId: side.id,
    playerIds: side.playerIds,
    groupId: groupOf(ctx, side.playerIds[0]!)?.id ?? '',
    perHole,
    thru: scored,
    complete,
    rankValue: u,
    detail: {
      kind: 'matchPlay',
      up: u,
      remaining,
      dormie: !decidedAt && u !== 0 && Math.abs(u) === remaining && remaining > 0,
      closedOut: decidedAt,
      won: w,
      lost: l,
      halved,
    },
    chip: {
      primary: label(u),
      secondary: decidedAt ? 'F' : calledIt ? 'called' : thruLabel(scored, holes.length),
      tone: u > 0 ? 'win' : u < 0 ? 'lose' : 'even',
    },
    counters,
  })

  return [
    mk(a, perHoleA, up, countersA, won, lost),
    mk(b, perHoleB, -up, countersB, lost, won),
  ]
}

/**
 * A two-man side's BETTER net on a hole — four-ball.
 *
 * The asymmetry with combinedBall below is the whole format. There, one man
 * failing to post kills the side's number, because the number is a sum. Here a
 * partner who picks up or is conceded takes only his own ball out of the hole;
 * the side plays on with the other one. Forfeiting the side would make every
 * four-ball match turn on whichever partner gave up first, which is precisely
 * the situation the format exists to rescue.
 *
 * A man still on the hole leaves it open even when his partner has already
 * conceded it, because the ball that decides the hole may not have been struck
 * yet — settling on the partner's forfeit would hand away holes that get won.
 */
function lowBall(ctx: RoundContext, playerIds: PlayerId[], i: number): Ball {
  const balls = playerIds.map((pid) =>
    ballFor(ctx.scores[pid]?.[i], ctx.pops[pid]?.[i] ?? 0),
  )
  if (balls.some((b) => b.pending)) return PENDING

  // A picked-up ball with a most-likely still cannot WIN a hole, so it is not a
  // candidate here even though stroke play and quota will happily post it.
  const live = balls.filter((b) => !b.forfeit && b.net != null)
  if (!live.length) return { net: null, gross: null, forfeit: true, pending: false }

  const best = live.reduce((x, y) => (y.net! < x.net! ? y : x))
  return { net: best.net, gross: best.gross, forfeit: false, pending: false }
}

/**
 * A two-man side's combined net on a hole. Either man giving up forfeits it.
 *
 * "Giving up" is not the same as "has not been written down yet", and this
 * conflated them. A pending ball carries `net: null` exactly as a picked-up one
 * does, so a side whose second man simply had not been entered was forfeiting
 * the hole — a1 makes 3 net, a2 is still being walked in, the opponents make 12
 * between them, and the side that won by six goes one down.
 *
 * Worse than a wrong number on screen: with closeout on, a scorer a few holes
 * behind his playing partner could run the match to "won 5 & 4" against the
 * side that was actually winning.
 *
 * So pending is tested first, as `lowBall` above already does. Only a ball with
 * a real state and no usable number — picked up, conceded — forfeits.
 */
function combinedBall(ctx: RoundContext, playerIds: PlayerId[], i: number) {
  const balls = playerIds.map((pid) =>
    ballFor(ctx.scores[pid]?.[i], ctx.pops[pid]?.[i] ?? 0),
  )
  if (balls.some((b) => b.pending)) {
    return { net: null, gross: null, forfeit: false, pending: true }
  }
  if (balls.some((b) => b.net == null)) {
    return { net: null, gross: null, forfeit: true, pending: false }
  }
  return {
    net: sum(balls.map((b) => b.net!)),
    gross: sum(balls.map((b) => b.gross ?? 0)),
    forfeit: false,
    pending: false,
  }
}

function settleHeadToHead(
  match: Match,
  pairs: [UnitTab, UnitTab][],
  payout: IndividualMatchPlayConfig['payout'],
): Settlement {
  const awards: Award[] = []
  for (const [a, b] of pairs) {
    for (const [tab, other] of [
      [a, b],
      [b, a],
    ] as const) {
      const diff = tab.rankValue - other.rankValue
      const bucket = diff > 0 ? payout.win : diff < 0 ? payout.loss : payout.halve
      const label =
        diff > 0 ? tab.chip.primary : diff < 0 ? 'Lost' : 'Halved'
      for (const pid of tab.playerIds) {
        awards.push({
          playerId: pid,
          matchId: match.id,
          place: diff > 0 ? 1 : diff < 0 ? 2 : 1,
          points: tab.complete ? bucket.points : 0,
          cash: tab.complete ? bucket.cash : 0,
          label,
        })
      }
    }
  }
  return { matchId: match.id, awards, tiebreaks: [] }
}

// ---------------------------------------------------------------------------
// team high / low
// ---------------------------------------------------------------------------

/**
 * Points accrue PER HOLE, not per match: the sheet says "After each hole, both
 * players on a team record their individual NET scores", so a nine-hole match
 * is worth up to 18 points rather than 2.
 */
function tabulateHighLow(
  ctx: RoundContext,
  cfg: TeamHighLowConfig,
  a: MatchPlaySide,
  b: MatchPlaySide,
  counterBasis: 'gross' | 'net',
  /** Called on darkness — the points as they stand decide it. */
  called = false,
): [UnitTab, UnitTab] {
  const perHoleA: HoleTab[] = []
  const perHoleB: HoleTab[] = []
  let ptsA = 0
  let ptsB = 0
  let lowA = 0
  let highA = 0
  let lowB = 0
  let highB = 0
  // Holes with a result, as distinct from the index of the furthest one.
  let scored = 0

  for (let i = 0; i < ctx.holes.length; i++) {
    const ta = baseHoleTab(ctx, i, a.playerIds)
    const tb = baseHoleTab(ctx, i, b.playerIds)

    const netsA = a.playerIds
      .map((pid) => ballFor(ctx.scores[pid]?.[i], ctx.pops[pid]?.[i] ?? 0))
      .map((x) => x.net)
    const netsB = b.playerIds
      .map((pid) => ballFor(ctx.scores[pid]?.[i], ctx.pops[pid]?.[i] ?? 0))
      .map((x) => x.net)

    // Both balls from both teams are needed to compare a low AND a high, so an
    // incomplete hole is excluded rather than guessed at.
    const ready =
      netsA.every((n) => n != null) && netsB.every((n) => n != null) && netsA.length === 2 && netsB.length === 2
    if (!ready) {
      perHoleA.push(ta)
      perHoleB.push(tb)
      continue
    }
    scored += 1

    const [aLow, aHigh] = [Math.min(...(netsA as number[])), Math.max(...(netsA as number[]))]
    const [bLow, bHigh] = [Math.min(...(netsB as number[])), Math.max(...(netsB as number[]))]

    let holeA = 0
    let holeB = 0
    if (aLow < bLow) { holeA += cfg.pointsPerHole.low; lowA += 1 }
    else if (bLow < aLow) { holeB += cfg.pointsPerHole.low; lowB += 1 }
    else { holeA += cfg.tieValue; holeB += cfg.tieValue }

    if (aHigh < bHigh) { holeA += cfg.pointsPerHole.high; highA += 1 }
    else if (bHigh < aHigh) { holeB += cfg.pointsPerHole.high; highB += 1 }
    else { holeA += cfg.tieValue; holeB += cfg.tieValue }

    ptsA += holeA
    ptsB += holeB
    ta.outcome = holeA > holeB ? 1 : holeA < holeB ? -1 : 0
    tb.outcome = ta.outcome == null ? null : -ta.outcome
    perHoleA.push(ta)
    perHoleB.push(tb)
  }

  // Every hole scored, not merely the last one — see the note in match play.
  // A dropped hole here costs up to two points, which is a seed.
  const complete = scored >= ctx.holes.length || (called && scored > 0)
  const calledIt = complete && scored < ctx.holes.length
  const mk = (
    side: MatchPlaySide,
    perHole: HoleTab[],
    mine: number,
    theirs: number,
    low: number,
    high: number,
  ): UnitTab => ({
    unitId: side.id,
    playerIds: side.playerIds,
    groupId: groupOf(ctx, side.playerIds[0]!)?.id ?? '',
    perHole,
    thru: scored,
    complete,
    rankValue: mine,
    detail: {
      kind: 'pointsPerHole',
      points: mine,
      opponentPoints: theirs,
      breakdown: { low, high },
    },
    chip: {
      primary: `${mine} - ${theirs}`,
      secondary: calledIt ? 'called' : thruLabel(scored, ctx.holes.length),
      tone: mine > theirs ? 'win' : mine < theirs ? 'lose' : 'even',
    },
    counters: countersForPlayers(ctx, side.playerIds, counterBasis),
  })

  return [
    mk(a, perHoleA, ptsA, ptsB, lowA, highA),
    mk(b, perHoleB, ptsB, ptsA, lowB, highB),
  ]
}

// ---------------------------------------------------------------------------
// quota
// ---------------------------------------------------------------------------

/**
 * Quota points come off GROSS versus par.
 *
 * The handicap is already absorbed by the starting target — the sheet says each
 * golfer "starts with a point value based on their handicap". Scoring the holes
 * off net as well would count the handicap twice and make every number in
 * Matches 4 and 6 wrong.
 */
export function quotaPointsForHole(
  value: HoleScoreValue | undefined,
  par: number,
  table: QuotaConfig['table'],
): number {
  if (!value || value.state !== 'holed') return table.worse
  const d = value.gross - par
  if (d <= -2) return table.eagleOrBetter
  if (d === -1) return table.birdie
  if (d === 0) return table.par
  if (d === 1) return table.bogey
  return table.worse
}

function tabulateQuota(
  ctx: RoundContext,
  cfg: QuotaConfig,
  playerId: PlayerId,
  counterBasis: 'gross' | 'net',
  /** Called on darkness — the target shrinks to the holes actually played. */
  called = false,
): UnitTab {
  const perHole: HoleTab[] = []
  const points: number[] = []
  let earned = 0

  for (let i = 0; i < ctx.holes.length; i++) {
    const tab = baseHoleTab(ctx, i, [playerId])
    const value = ctx.scores[playerId]?.[i]
    const p = value ? quotaPointsForHole(value, ctx.holes[i]!.par, cfg.table) : 0
    if (value) earned += p
    points.push(value ? p : 0)
    tab.outcome = value ? (p >= cfg.table.par ? 1 : p === cfg.table.bogey ? 0 : -1) : null
    perHole.push(tab)
  }

  // RoundHandicap.courseHandicap is ALREADY the figure for the holes this round
  // plays — see the convention note in buildContext(). Pro-rating it here halved
  // a nine that had already been halved, so a man popping eleven times played to
  // a target that had only taken six off him and the format contradicted itself.
  const hc = ctx.handicaps[playerId]
  const fullTarget = quotaTarget(
    cfg,
    ctx.holes.length,
    hc?.courseHandicap ?? 0,
    hc?.quotaTarget ?? null,
  )
  const thru = thruFor(ctx, [playerId])
  /*
   * A called round shrinks the target with the day.
   *
   * The full target prices a full round's handicap. Hold a man to it over
   * twelve holes and the horn costs the high handicaps most — the exact men
   * the target exists to protect — while a scratch player barely notices.
   * So the whole target is prorated to the holes he answered, in one rounding,
   * which also scales an explicit committee-set quota rather than ignoring it.
   */
  const calledIt = called && thru > 0 && thru < ctx.holes.length
  const target = calledIt
    ? Math.round((fullTarget * thru) / ctx.holes.length + Number.EPSILON)
    : fullTarget
  const delta = earned - target
  const base = calledIt
    ? cfg.quotaFormula.pointsPerPar * thru
    : cfg.quotaFormula.pointsPerPar * ctx.holes.length
  const banked = base - target

  return {
    unitId: unitId('q', playerId),
    playerIds: [playerId],
    groupId: groupOf(ctx, playerId)?.id ?? '',
    perHole,
    thru,
    complete: thru >= ctx.holes.length || calledIt,
    rankValue: delta,
    detail: { kind: 'quota', target, earned, delta, perHole: points },
    /*
     * Shown the way the group says it out loud: you start with points for your
     * handicap and add what you earn, and everybody is chasing the same number.
     *
     * The engine ranks on `delta`, and it always will — that is the figure with
     * a sign on it. But the board used to PRINT the delta over "earned /
     * target", which quietly told a high handicapper his target was 18 when a
     * scratch man's was 29, and a smaller number reads as being behind. It is
     * the opposite: the low target IS the head start. Ranking is unchanged
     * either way, since everyone in a round shares the same base — but only one
     * of the two framings matches the scorecard in somebody's back pocket, and
     * the group chat has always used that one.
     *
     * `banked` is derived from the target rather than from the course handicap
     * so it stays honest when a card sets an explicit quota for somebody.
     */
    chip: {
      primary: `${banked + earned}`,
      /*
       * The sum, not the shortfall — Scott's ruling from Old Macdonald.
       *
       * This read "of 18 · −14" through most of a round, because the target
       * prices a whole nine and a man three holes in has earned three holes'
       * points. Every man is a big negative until the last green, which
       * makes the board look like a field of failures and buries the one
       * number he wants: where he stands. So it says what he started with
       * and what he has earned since, and the total above it is their sum.
       */
      secondary: `${banked} + ${earned}${calledIt ? ' · called' : ''}`,
      tone: delta > 0 ? 'win' : delta < 0 ? 'lose' : 'even',
    },
    counters: countersForPlayers(ctx, [playerId], counterBasis),
  }
}

// ---------------------------------------------------------------------------
// net stroke play
// ---------------------------------------------------------------------------

function tabulateStrokeNet(
  ctx: RoundContext,
  cfg: StrokeNetConfig,
  playerId: PlayerId,
  counterBasis: 'gross' | 'net',
): UnitTab {
  const perHole: HoleTab[] = []
  /** Holes the app posted a figure for, because nobody holed one out. */
  const capped: number[] = []
  let grossTotal = 0
  let parTotal = 0
  let thru = 0

  for (let i = 0; i < ctx.holes.length; i++) {
    const hole = ctx.holes[i]!
    const tab = baseHoleTab(ctx, i, [playerId])
    const value = ctx.scores[playerId]?.[i]

    // Nothing entered: the man is still out there. A maximum here would be an
    // invention, and the running total has to stay a real number all afternoon.
    if (!value) {
      perHole.push(tab)
      continue
    }
    thru = i + 1

    const holedOut =
      value.state === 'holed' ? value.gross : value.state === 'pickedUp' ? value.mostLikely : null

    /*
     * A hole PLAYED but never holed out — conceded, or picked up with nobody
     * willing to name a most-likely — posts `par + maxOverParNet`. Leaving it out
     * of the total is what let a man who walked in on the 18th beat a man who
     * finished, because the hole he skipped cost him nothing at all.
     *
     * No `+ pops`, and nothing here CAPS a hole that was holed out. Both were
     * removed together when the championship stopped drawing pop holes: the
     * handicap is spent once, up front, as the number you start under par, so
     * there is no per-hole allowance left to raise a ceiling with — and a blow-up
     * hole costs full freight, which is how the group plays it. Confirmed with
     * Scott, 2026-08-11.
     *
     * So `maxOverParNet` is now only the value a NOT-HOLED-OUT hole posts. The
     * name outlived its meaning and is deliberately not being changed twelve days
     * before the trip: it is a key inside two stored tournament documents, and a
     * rename is a data migration for no behavioural gain.
     */
    const unfinished = cfg.maxOverParNet == null ? null : hole.par + cfg.maxOverParNet
    /*
     * The ceiling on a hole a man DID finish — the triple-bogey max.
     *
     * Applied to the gross and nowhere near the allowance: the championship
     * draws no pop holes and spends the handicap once, off the total, so a cap
     * on the net would take the same strokes off twice. A nine on a par four
     * counts as a seven and the starting figure is untouched.
     */
    const ceiling = cfg.maxOverParGross == null ? null : hole.par + cfg.maxOverParGross
    const finished = holedOut == null || ceiling == null ? holedOut : Math.min(holedOut, ceiling)
    const posted = finished ?? unfinished

    // With no posting rule there is no defensible number, so the total is
    // knowingly short a hole. This is what types.ts's unused
    // INCOMPLETE_STROKE_TOTAL exists to say, but a UnitTab carries no warning
    // channel to say it on.
    if (posted == null) {
      perHole.push(tab)
      continue
    }

    // Now only ever true for a hole nobody holed out, which is worth surfacing —
    // it is a figure the app supplied rather than one anybody made.
    if (holedOut == null && posted != null) capped.push(hole.number)
    grossTotal += posted
    parTotal += hole.par
    tab.gross[playerId] = posted
    // No pops, so the net of a single hole IS its gross. The allowance is not
    // spread across the card; it is taken off the total once, below.
    tab.net[playerId] = posted
    perHole.push(tab)
  }

  /*
   * The allowance, once, off the total.
   *
   * This is the whole shape of the format: you start a set number under par and
   * play gross. It used to net each hole against that hole's own pop, which came
   * to the same total but only because the pops summed to the handicap — and it
   * put strokes on the card that the group does not play.
   *
   * Taken from the round's handicap snapshot, which for an eighteen-hole round is
   * an eighteen-hole figure. THE CONVENTION holds: whatever is stored is already
   * scoped to the holes this round plays, so it is never halved again here.
   *
   * A man yet to tee off therefore sits at minus his allowance, which is exactly
   * what "you start five under" means and is what the chip shows before a ball is
   * struck. Mid-round it flatters him, because the whole allowance is spent by
   * the time he has played one hole — that is inherent to a starting figure and is
   * true of the quota rounds too.
   */
  const allowance = ctx.handicaps[playerId]?.courseHandicap ?? 0
  const netTotal = grossTotal - allowance

  const toParNet = netTotal - parTotal

  return {
    unitId: unitId('s', playerId),
    playerIds: [playerId],
    groupId: groupOf(ctx, playerId)?.id ?? '',
    perHole,
    thru,
    complete: thru >= ctx.holes.length,
    // Lower net is better, so negate to keep rankValue higher-is-better.
    rankValue: -netTotal,
    detail: { kind: 'strokes', grossTotal, netTotal, toParNet, cappedHoles: capped },
    chip: {
      primary: toParNet === 0 ? 'E' : `${toParNet > 0 ? '+' : ''}${toParNet}`,
      /*
       * Big figure net, small figure GROSS — Scott's calibration.
       *
       * The line under the number used to repeat the net, as a stroke total:
       * "net 14". That is the same fact twice, in a unit nobody standing on a
       * tee thinks in, and it left the one number a man actually wants beside
       * his net — what he has ACTUALLY shot — nowhere on the board. Thomas
       * reading "-5 · net 14" could not tell you he was three over par.
       *
       * Gross to par, so it sits in the same unit as the net above it and the
       * handicap between them is legible at a glance: +3 gross, -8 start, -5 net.
       */
      secondary:
        thru === 0
          ? '—'
          : `gross ${
              grossTotal - parTotal === 0
                ? 'E'
                : `${grossTotal - parTotal > 0 ? '+' : ''}${grossTotal - parTotal}`
            } · ${thruLabel(thru, ctx.holes.length)}`,
      tone: toParNet < 0 ? 'win' : toParNet > 0 ? 'lose' : 'even',
    },
    counters: countersForPlayers(ctx, [playerId], counterBasis),
  }
}

// ---------------------------------------------------------------------------
// driver
// ---------------------------------------------------------------------------

export function runMatch(
  ctx: RoundContext,
  match: Match,
  tb: TiebreakContext,
  seeding: Seeding = {},
): MatchResult {
  const cfg: FormatConfig = match.config
  const basis = ctx.tournament.settings.tiebreakCounterBasis
  const chain = ctx.tournament.settings.standingsTiebreak

  if (
    cfg.kind === 'individualMatchPlay' ||
    cfg.kind === 'teamCombinedNet' ||
    cfg.kind === 'teamFourBall'
  ) {
    const mode: SideBall =
      cfg.kind === 'individualMatchPlay'
        ? 'single'
        : cfg.kind === 'teamCombinedNet'
          ? 'combined'
          : 'lowBall'
    const pairs: [UnitTab, UnitTab][] = pairingsFor(ctx, match).map((p) =>
      tabulateMatchPlay(
        ctx,
        { id: p.sides[0].id, playerIds: p.sides[0].playerIds },
        { id: p.sides[1].id, playerIds: p.sides[1].playerIds },
        cfg.closeout,
        basis,
        mode,
        match.calledAt != null,
      ),
    )
    return {
      tabs: pairs.flat(),
      settlement: settleHeadToHead(match, pairs, cfg.payout),
    }
  }

  if (cfg.kind === 'teamHighLow') {
    const pairs: [UnitTab, UnitTab][] = pairingsFor(ctx, match).map((p) =>
      tabulateHighLow(
        ctx,
        cfg,
        { id: p.sides[0].id, playerIds: p.sides[0].playerIds },
        { id: p.sides[1].id, playerIds: p.sides[1].playerIds },
        basis,
        match.calledAt != null,
      ),
    )
    return {
      tabs: pairs.flat(),
      settlement: settleHeadToHead(match, pairs, cfg.payout),
    }
  }

  if (cfg.kind === 'quota') {
    const players = ctx.groups.flatMap((g) => g.playerIds)
    const tabs = players.map((pid) => tabulateQuota(ctx, cfg, pid, basis, match.calledAt != null))
    // The ONLY difference between Match 4 and Match 6: what you are ranked
    // against. Same module, so the two can never drift apart.
    const buckets =
      cfg.rankingScope === 'field'
        ? [tabs]
        : ctx.groups.map((g) => tabs.filter((t) => g.playerIds.includes(t.playerIds[0]!)))

    const awards: Award[] = []
    const tiebreaks: TiebreakResolution[] = []
    buckets.forEach((bucket, i) => {
      const r = rankAndAward(
        match.id,
        bucket,
        cfg.payouts,
        chain,
        tb,
        `match:${match.id}:${cfg.rankingScope === 'field' ? 'field' : i}`,
      )
      awards.push(...r.awards)
      tiebreaks.push(...r.tiebreaks)
    })
    return { tabs, settlement: { matchId: match.id, awards, tiebreaks } }
  }

  // strokeNet
  const players = ctx.groups.flatMap((g) => g.playerIds)
  const tabs = players.map((pid) => tabulateStrokeNet(ctx, cfg, pid, basis))
  const awards: Award[] = []
  const tiebreaks: TiebreakResolution[] = []

  if (cfg.flighted && cfg.flights.length) {
    // Each flight is its own competition with its own purse.
    cfg.flights.forEach((flight) => {
      const inFlight = tabs.filter((t) => {
        // A player nobody seeded cannot be flighted — better that he is visibly
        // missing from the purse than quietly paid out of the wrong one.
        const seed = seeding[t.playerIds[0]!]
        return seed != null && seed >= flight.fromSeed && seed <= flight.toSeed
      })
      const r = rankAndAward(
        match.id,
        inFlight,
        flight.payouts,
        chain,
        tb,
        `match:${match.id}:${flight.name}`,
      )
      awards.push(
        ...r.awards.map((a) => ({
          ...a,
          label: a.place === 1 ? `${flight.name} Winner` : `${flight.name} ${a.label}`,
        })),
      )
      tiebreaks.push(...r.tiebreaks)
    })
  } else {
    const r = rankAndAward(match.id, tabs, [], chain, tb, `match:${match.id}`)
    awards.push(...r.awards)
    tiebreaks.push(...r.tiebreaks)
  }

  return { tabs, settlement: { matchId: match.id, awards, tiebreaks } }
}
